import dayjs from '@bunce/dayjs'
import type { AxiosProgressEvent } from 'axios'
import type { MaybeRefOrGetter } from '@vueuse/core'
// import type { DataTableProps } from 'primevue/datatable'
import type { PaginatedResponseInterface } from '~/types'

export * from './object-to-formdata'
export * from './currency'
export * from './pages'
export * from './automation'
export * from './broadcast'
export * from './segment'

export function currencyDisplay(amount: number, currency = 'NGN') {
  return amount.toLocaleString('en-NG', {
    currency,
    style: 'currency',
  })
}

export function formatNumberToK(amount: number): string {
  if (amount >= 1_000_000) {
    return (amount / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'm'
  } else if (amount >= 1000) {
    return (amount / 1000).toFixed(1).replace(/\.0$/, '') + 'k'
  }
  return amount.toString()
}

export const pluralize = (
  amount: number,
  singular: any,
  plural: any,
  includeNumber = true,
) => {
  const response = amount > 1 || amount === 0 ? plural : singular
  if (includeNumber) return `${amount} ${response}`
  return response
}

export const capitalize = (str: string): string => str.toUpperCase()

export const formatDateToDateString = (
  value: string | Date,
  formatter = 'DD MMM YY',
): string => dayjs(new Date(value)).format(formatter)

export const isEmpty = (str: string): boolean => !str.trim().length

export const firstLetter = (value: string): string => value[0].toUpperCase()

export const formatAmount = (value = 0, currency = 'NGN'): string => {
  return Number(value || 0).toLocaleString('en', {
    style: 'currency',
    currencyDisplay: 'narrowSymbol',
    currency: currency || 'NGN',
  })
}

export const copyToClipboard = (text: string): void => {
  const input = window.document.createElement('input')
  input.value = text
  input.style.display = 'none'
  window.document.body.appendChild(input)

  input.select()
  input.setSelectionRange(0, 999999999)
  navigator.clipboard
    .writeText(text)
    .then(() => toast.success('Text copied to clipboard'))

  input.remove()

  // nuxtApp.$toast.success('Text Copied')
}

export const words = (
  value: string,
  words: number = 100,
  end: string = '...',
): string => {
  const regex = new RegExp(`^\\s*(?:\\S+\\s+){1,${words}}`, 'u')
  const matches = value.match(regex)

  if (!matches || value.length === matches[0].length) {
    return value
  }

  return matches[0].trimEnd() + end
}

export const addThousandSeparator = (
  integer: string | number,
  separator?: string,
): string =>
  (integer ?? 0)
    .toString()
    .replace(/(\d)(?=(?:\d{3})+\b)/gm, `$1${separator ?? ','}`)

/**
 * @link https://forum.bubble.io/t/text-to-url-slug-with-regex/63920/9
 * @param text
 */
export function slugify(text: string, seperator = '-') {
  return text
    .toLowerCase()
    .replace(/\s+/g, seperator) // Replace spaces with -
    .replace(/[^\w\-]+/g, '') // Remove all non-word chars
    .replace(/\-\-+/g, seperator) // Replace multiple - with single -
    .replace(/^-+/, '') // Trim - from start of text
    .replace(/-+$/, '') // Trim - from end of text
}

export function getUploadProgressPercentageFromEvent(event: AxiosProgressEvent) {
  return Math.floor((event.loaded * 100) / (event.total || 0))
}

export function axiosDownloader(response: any, name = 'download.csv'): void {
  const fileURL = window.URL.createObjectURL(new Blob([response.data]))
  const fileLink = document.createElement('a')
  fileLink.href = fileURL

  fileLink.setAttribute('download', name)
  document.body.appendChild(fileLink)

  fileLink.click()
}

export function exportToCsv(
  filename: string,
  rows: Array<Array<string>>,
  options?: { downloadable?: boolean },
): void | File {
  const processRow = (row: Array<any>) => {
    let finalVal = ''
    for (let j = 0; j < row.length; j++) {
      let innerValue = row[j] === null ? '' : row[j].toString()
      if (row[j] instanceof Date) {
        innerValue = row[j].toLocaleString()
      }
      let result = innerValue.replace(/"/g, '""')
      if (result.search(/("|,|\n)/g) >= 0) result = '"' + result + '"'
      if (j > 0) finalVal += ','
      finalVal += result
    }
    return finalVal + '\n'
  }

  let csvFile = ''
  for (let i = 0; i < rows.length; i++) {
    csvFile += processRow(rows[i])
  }

  const blob = new Blob([csvFile], { type: 'text/csv;charset=utf-8;' })

  if (options?.downloadable) {
    return new File([blob], filename, { type: 'text/csv' })
  }

  // @ts-expect-error should exists
  if (navigator.msSaveBlob) {
    // IE 10+
    // @ts-expect-error should exists
    navigator.msSaveBlob(blob, filename)
  } else {
    const link = document.createElement('a')
    if (link.download !== undefined) {
      // feature detection
      // Browsers that support HTML5 download attribute
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', filename)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }
}

export function useFormattedDateTime(
  value: MaybeRefOrGetter<string | number | Date | undefined | null>,
  options: Intl.DateTimeFormatOptions = {
    dateStyle: 'long',
    timeStyle: 'medium',
  },
) {
  const { locale } = useI18n()
  const formatter = $computed(() =>
    Intl.DateTimeFormat(locale.value ?? 'en', options),
  )
  return computed(() => {
    const v = resolveUnref(value)
    return v ? formatter.format(new Date(v)) : ''
  })
}

/**
 * Transforms array of objects containing `id` attribute,
 * or array of ids (strings), into a Map, keyd by `id`.
 */
export const arrayToMap = <T extends { id: string } | string>(
  items: readonly T[],
) => {
  return items.reduce((acc: Map<string, T>, element) => {
    acc.set(typeof element === 'string' ? element : element.id, element)
    return acc
  }, new Map())
}

export const validateArrayValues = (array: (string | null)[][]): boolean => {
  for (const row of array) {
    for (const value of row) {
      if (value === null || value === '') {
        return false
      }
    }
  }
  return true
}

export const toInternationalPhoneNumber = (
  phone: MaybeRef<string>,
  code: MaybeRef<string>,
): string => {
  const toValue = `${parseInt(unref(phone))}`

  const phoneNumber = !toValue.startsWith(unref(code))
    ? `${unref(code)}${parseInt(toValue)}`
    : toValue

  return phoneNumber?.replace(/^undefined/, '')
}

export function makeDataTablePaginationProps(
  result?: PaginatedResponseInterface<any>,
) {
  return {
    value: result?.data || [],
    paginator: true,
    lazy: true,
    first: (result?.meta?.from ?? result?.from ?? 1) - 1,
    rows: result?.meta?.per_page || result?.per_page,
    totalRecords: result?.meta?.total || result?.total,
    paginatorTemplate:
      'RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink',
    currentPageReportTemplate: '{first} to {last} of {totalRecords}',
  }
}

export function getSlackOauthUrl() {
  return useRuntimeConfig().public.baseUrl + '/integrations/slack/auth'
}

export function isValidWorkEmail(email: string): boolean {
  const emailDomain = email.split('@')[1]

  const excludedDomains = [
    'gmail.com',
    'yahoo.com',
    'hotmail.com',
    'outlook.com',
    'aol.com',
    'icloud.com',
    'mail.com',
    'protonmail.com',
    'zoho.com',
    'live.com',
    'gmx.com',
    'yandex.com',
    'inbox.com',
    'me.com',
    'rocketmail.com',
    'fastmail.com',
    'qq.com',
    '163.com',
    '126.com',
    'sina.com',
    'sohu.com',
    'naver.com',
    'daum.net',
    'hanmail.net',
    'nate.com',
    't-online.de',
    'web.de',
    'gmx.de',
    'yahoo.co.jp',
    'softbank.jp',
    'docomo.ne.jp',
    'ezweb.ne.jp',
    'au.com',
    'bigpond.com',
    'optusnet.com.au',
    'gmx.net',
    't-online.com',
    'web.de',
    'outlook.de',
    'aol.de',
    'mail.ru',
    'rambler.ru',
    'yandex.ru',
    'abv.bg',
    'mail.bg',
    'tiscali.it',
    'libero.it',
    'virgilio.it',
    'alice.it',
    'tim.it',
    'orange.fr',
    'sfr.fr',
    'free.fr',
    'laposte.net',
    'wanadoo.fr',
    'gmx.fr',
    'webmail.co.za',
    'telkomsa.net',
    'vodamail.co.za',
    'lycos.com',
    'mail.lycos.com',
    'hushmail.com',
    // 'mailinator.com',
    'tutanota.com',
    'hush.com',
    'lavabit.com',
    'gawab.com',
    'bluewin.ch',
    'tiscali.co.uk',
    'btinternet.com',
    'virginmedia.com',
    'talktalk.net',
    'icloud.co.uk',
    'me.com',
    'bigmir.net',
    'hotmail.co.uk',
    'ymail.com',
    'foxmail.com',
    'citromail.hu',
    'freemail.hu',
    'indamail.hu',
    'mailchimp.com',
    'example.com',
  ]

  const domainRegex = new RegExp(
    `^(?!${excludedDomains
      .map((domain) => `.*${domain}`)
      .join('|')}$)[a-z0-9]+(?:-[a-z0-9]+)*\.[a-z]{2,}(?:\.[a-z]{2,})?$`,
    'i',
  )

  return domainRegex.test(emailDomain)
}

export function abbreviateNumber(value: number) {
  if (value === 0) {
    return value // If the input value is 0, return it directly
  }

  const suffixes = ['', 'k', 'M', 'B', 'T']
  const order = value === 0 ? 0 : Math.floor(Math.log10(Math.abs(value)) / 3)

  if (order >= 0 && order < suffixes.length) {
    const suffix = suffixes[order]
    const abbreviatedValue = value / Math.pow(10, order * 3)

    // If the order is 2 (hundreds of thousands), append 'k' to the value
    if (order === 2) {
      return `${abbreviatedValue}k`
    } else {
      return `${abbreviatedValue}${suffix}`
    }
  } else {
    return value // If the order exceeds the available suffixes or is negative, return the original value
  }
}

export const formatToRelativeTime = (dateString: string) => {
  const date = dayjs(dateString)

  const calendarString = date.calendar(null, {
    sameDay: '[today at] h:mm A',
    lastDay: '[yesterday at] h:mm A',
    lastWeek: '[last] dddd [at] h:mm A',
    sameElse: 'MMMM D, YYYY [at] h:mm A',
  })

  return `Created ${calendarString}`
}

export const logger = import.meta.dev ? console.log : (...msg: any) => {}

export function getFullName(user: {
  first_name?: string | null | undefined
  last_name?: string | null | undefined
}) {
  return `${user?.first_name ?? '-------'} ${user?.last_name ?? '-------'}`
}

export function convertUrlsToHTMLAnchors(text: string) {
  if (!text) return ''

  const urlRegex = /(\b(https?:\/\/|www\.)[^\s]+[^\s.,!?"')])/g

  return text.replace(urlRegex, (url) => {
    let formattedUrl = url

    if (!formattedUrl.startsWith('http')) {
      formattedUrl = 'https://' + formattedUrl
    }

    return `<a href="${formattedUrl}" target="_blank" class="text-bunce-primary underline font-medium">${url}</a>`
  })
}

export function getRandomArrayValue<T = any>(arr: Array<T>) {
  const randomIndex = Math.floor(Math.random() * arr.length)

  return arr.at(randomIndex)!
}
